import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getGrapeVarietiesFilterOptions } from '../filters/grapeVarieties';
import { getYearFilterOptions } from '../filters/year';

function Filters({ applyFilter, currentFilters, filteredWines, resetFilters, onToggle }) {

  const { language } = useLanguage();
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const savedState = localStorage.getItem('expandedCategories');
    return savedState ? JSON.parse(savedState) : {};
  });

  useEffect(() => {
    localStorage.setItem('expandedCategories', JSON.stringify(expandedCategories));
  }, [expandedCategories]);

  const filterCategories = [
      {
        name: 'type',
        options: [
          { value: 'all', label: { en: 'All', pt: 'Todos' } },
          { value: 'red', label: { en: 'Red', pt: 'Tinto' } },
          { value: 'white', label: { en: 'White', pt: 'Branco' } },
          { value: 'rose', label: { en: 'Rosé', pt: 'Rosé' } },
          { value: 'sparkling', label: { en: 'Sparkling', pt: 'Espumante' } },
          { value: 'champagne', label: { en: 'Champagne', pt: 'Champagne' } },
          { value: 'porto', label: { en: 'Port', pt: 'Porto' } }
        ]
      },
      {
        name: 'size',
        options: [
          { value: 'all', label: { en: 'All', pt: 'Todos' } },
          { value: 'standard', label: { en: 'Standard (0.75L)', pt: 'Padrão (0.75L)' } },
          { value: 'magnum', label: { en: 'Magnum (1.5L)', pt: 'Magnum (1.5L)' } }
        ]
      },
      {
        name: 'producer',
        options: [
          { value: 'all', label: { en: 'All', pt: 'Todos' } },
          { value: 'monteirinhos', label: 'Quinta dos Monteirinhos' },
          { value: 'pessegueiro', label: 'Quinta do Pessegueiro' },
          { value: 'couquinho', label: 'Quinta do Couquinho' },
          { value: 'mumm', label: 'Mumm' }
        ]
      },
      {
        name: 'region',
        options: [
          { value: 'all', label: { en: 'All', pt: 'Todos' } },
          { value: 'douro', label: 'Douro' },
          { value: 'dao', label: 'Dão' },
          { value: 'champagne', label: 'Champagne' },
        ]
      },
      {
        name: 'priceRange',
        options: [
          { value: 'all', label: { en: 'All', pt: 'Todos' } },
          { value: 'range_0_5', label: { en: '<5€', pt: '<5€' } },
          { value: 'range_5_10', label: { en: '5-10€', pt: '5-10€' } },
          { value: 'range_10_20', label: { en: '10-20€', pt: '10-20€' } },
          { value: 'range_20_35', label: { en: '20-35€', pt: '20-35€' } },
          { value: 'range_35_50', label: { en: '35-50€', pt: '35-50€' } },
          { value: 'range_50_100', label: { en: '50-100€', pt: '50-100€' } },
          { value: 'range_over_100', label: { en: '>100€', pt: '>100€' } }
        ]
      },
      {
        name: 'year',
        options: getYearFilterOptions()
      },
      {
        name: 'grapes',
        multiSelect: true,
        options: getGrapeVarietiesFilterOptions(currentFilters, filteredWines)
      }
    ];

    const resetAll = () => {
      resetFilters();
      setExpandedCategories({});
    }

  const translateCategoryName = (categoryName) => {
    switch (categoryName) {
      case 'type':
        return 'Tipo';
      case'size':
        return 'Tamanho';
      case 'producer':
        return 'Produtor';
      case 'region':
        return 'Região';
      case 'year':
        return 'Ano';
      case 'grapes':
        return 'Castas';
      case 'priceRange':
        return 'Preço';
      default:
        return categoryName;
    }
  };

  const handleFilterClick = (category, value) => {
    if (category.multiSelect) {
      if (value === 'all') {
        applyFilter(category.name, []);
        return;
      }
      if (value === 'mono') {
        applyFilter(category.name, [value]);
        return;
      }
      const currentValues = currentFilters[category.name]?.filter(val => val !== 'all') || [];

      if (currentValues.includes('mono')) {
        applyFilter(category.name, ['mono', value]);
        return;
      }
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      applyFilter(category.name, newValues);
    } else {
      applyFilter(category.name, value);
    }
  }
  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const isActive = (category, value) => {
    if (category.multiSelect) {
      return (currentFilters[category.name] || []).includes(value);
    } else {
      return currentFilters[category.name] === value;
    }
  };

  const renderNameSearch = () => (
    <div className="filter-category">
      <input
        type="text"
        placeholder={language === 'en' ? 'Search...' : 'Pesquisar...'}
        value={currentFilters.name || ''}
        onChange={(e) => applyFilter('name', e.target.value)}
        className="name-search-input"
      />  
    </div>
  );

  return (
    <div className="filters">
      <button onClick={resetAll}>{language === 'pt' ? 'Limpar Tudo' : 'Reset all'}</button>
      {renderNameSearch()}
      {filterCategories.map(category => (
        <div key={category.name} className="filter-category">
          <h3 onClick={() => toggleCategory(category.name)}>
            {language === 'en' ? category.name.charAt(0).toUpperCase() + category.name.slice(1) : translateCategoryName(category.name)}
            <span className="expand-arrow">{expandedCategories[category.name] ? '▼' : '▶'}</span>
          </h3>

          {expandedCategories[category.name] && (
            <ul>
              {category.options.map(option => (
                <li key={option.value}>
                  <button
                    className={isActive(category, option.value) ? 'active' : ''}
                    onClick={() => handleFilterClick(category, option.value)}
                  >
                    {option.label?.[language] || option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

export default Filters;


